// src/components/Reports.js
import { useState, useRef, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from "../firebase-config";
import { Button } from '@mui/material'; 
import { Bar } from 'react-chartjs-2';
import html2canvas from 'html2canvas'; // Use this for better PDF rendering
import jsPDF from 'jspdf';
import "../style/Reports.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Reports = ({ teams, students, className, questionnaireId, onReadyToRender, autoGeneratePDF = false, hideControls = false, isArchived = false }) => {
  const [activeSubTab, setActiveSubTab] = useState('all');

  const contentRef = useRef(); // define the ref

  const [dailyProgress, setDailyProgress] = useState([]); 
  const [currentDayIndex, setCurrentDayIndex] = useState(0); 

  const [exportDialogOpen, setExportDialogOpen] = useState(false);


  const [includedSections, setIncludedSections] = useState({
    dailyProgress: true,
    teamConfirmation: true,
    teamComposition: true,
    unassignedStudents: true,
    questionnaireResponses: true,
    questionnaireFeedback: true,
    teamAllocationFeedback: true,
  });

  useEffect(() => {
    if (activeSubTab === 'all') {
      setIncludedSections({
        dailyProgress: true,
        teamConfirmation: true,
        teamComposition: true,
        unassignedStudents: true,
        questionnaireResponses: true,
        questionnaireFeedback: true,
        teamAllocationFeedback: true,
      });
    }
  }, [activeSubTab]);
  
  const handleToggleSection = (section) => {
    setIncludedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  
  // Filter students not in any team
  const unassignedStudents = students.filter(
    student => student.teamNumber === "N/A" || !student.teamNumber
  );

  // Calculate status counts
  const statusData = {
    accepted: students.filter(s => s.hasConfirmedGroup === true).length,
    pending: students.filter(s => 
        s.teamNumber !== "N/A" && 
        s.hasConfirmedGroup === false
    ).length,
    notStarted: students.filter(s => s.teamNumber === "N/A").length
};

  // Prepare data for the chart
  const chartData = {
    labels: ['Accepted', 'Pending', 'Not Started'],
    datasets: [
      {
        data: [
          statusData.accepted,
          statusData.pending,
          statusData.notStarted
        ],
        backgroundColor: [
          'rgba(75, 192, 75, 0.6)',   // Green for accepted
          'rgba(255, 206, 86, 0.6)',   // Yellow for pending
          'rgba(255, 99, 132, 0.6)'    // Red for not started
        ],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Team Grouping Status',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Students'
        },
        ticks: {
          stepSize: 1,         // Each grid represents 1 person
          precision: 0         // No decimal point
        },
      }
    }
  };

  const [feedbackList, setFeedbackList] = useState([]);

  // Load daily questionnaire completion
  useEffect(() => {
    const fetchResponses = async () => {
      try {
        if (!questionnaireId) return;
  
        const docRef = doc(db, 'Responses', questionnaireId);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) return;
  
        const { responses = [] } = docSnap.data();
  
        // 1. Arranged into { dateKey: Set<uid> } 
        const dateMap = {}; // e.g. { '2025-04-17': Set(['u1','u2']) }
        responses.forEach(resp => {
          if (!resp.timestamp) return;
  
          const ts = resp.timestamp.toDate?.()    // Firestore Timestamp?
                   ?? new Date(resp.timestamp);   // fallback string
          const key = ts.toISOString().split('T')[0];
  
          if (!dateMap[key]) dateMap[key] = new Set();
          dateMap[key].add(resp.uid);            // Using Set to Remove Duplicates
        });
  
        if (Object.keys(dateMap).length === 0) return;
  
        // 2. Create a complete date sequence
        const allDates = Object.keys(dateMap).map(d => new Date(d));
        const min = new Date(Math.min(...allDates));
        const max = new Date(Math.max(...allDates));
  
        const daily = [];
        const cumulativeSet = new Set();         // Accumulated completed UID
        const total = students.length;
  
        for (let d = new Date(min); d <= max; d.setDate(d.getDate() + 1)) {
          const key = d.toISOString().split('T')[0];
  
          // Add the people who finished today to cumulativeSet
          if (dateMap[key]) {
            dateMap[key].forEach(uid => cumulativeSet.add(uid));
          }
  
          daily.push({
            date: key,
            completed: cumulativeSet.size,       // Cumulative completion
            pending: total - cumulativeSet.size  // Remaining
          });
        }
  
        setDailyProgress(daily);
        setCurrentDayIndex(daily.length - 1);    // The default display is the latest day
      } catch (err) {
        console.error('Error fetching responses:', err);
      }
    };
  
    fetchResponses();
  }, [questionnaireId, students.length]);
  
  useEffect(() => {
    const fetchFeedback = async () => {
      if (!questionnaireId) return;
      try {
        const feedbackRef = doc(db, 'Feedback', questionnaireId); // 👉 from Feedback collection
        const feedbackSnap = await getDoc(feedbackRef);
        if (feedbackSnap.exists()) {
          setFeedbackList(feedbackSnap.data().questionnaireFeedback || []); // 👉 correct field
        } else {
          setFeedbackList([]);
        }
      } catch (error) {
        console.error('Error fetching feedback:', error);
      }
    };
  
    fetchFeedback();
  }, [questionnaireId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (onReadyToRender) {
        console.log("✅ onReadyToRender called from Reports");
        onReadyToRender();
      }
    }, 2000); // allow render time
  
    return () => clearTimeout(timer);
  }, []);

  // Generate daily chart data
  const dailyChartData = dailyProgress[currentDayIndex] ? {
    labels: ['Completed', 'Pending'],
    datasets: [
      {
        data: [dailyProgress[currentDayIndex].completed, dailyProgress[currentDayIndex].pending],
        backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(255, 99, 132, 0.6)']
      }
    ]
  } : null;

  const dailyChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Students Completed on ' + dailyProgress[currentDayIndex]?.date || ''
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: students.length,
        ticks: {
          stepSize: 1,         // Each grid represents 1 person
          precision: 0         // No decimal point
        },
        title: {
          display: true,
          text: 'Number of Students'
        }
      }
    }
  };

  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const downloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);
  
      const subtabs = document.querySelector('.reports-subtabs');
      /*const buttons = document.querySelectorAll('button');*/
  
      if (subtabs) subtabs.style.display = 'none';
      /*buttons.forEach(btn => btn.style.display = 'none');*/
  
      const element = contentRef.current;
      const canvas = await html2canvas(element, { scale: 2 }); // better quality
  
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
  
      const imgProps = {
        width: canvas.width,
        height: canvas.height,
      };
  
      const ratio = (pageWidth - 2 * margin) / imgProps.width;
      const imgScaledHeight = imgProps.height * ratio;
  
      let heightLeft = imgScaledHeight;
      let position = 0;
  
      while (heightLeft > 0) {
        const sourceY = position / imgScaledHeight * imgProps.height;
  
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = imgProps.width;
        pageCanvas.height = (pageHeight - 2 * margin) / ratio;
  
        const pageCtx = pageCanvas.getContext('2d');
        pageCtx.fillStyle = 'white';
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageCtx.drawImage(
          canvas,
          0, sourceY, imgProps.width, pageCanvas.height,
          0, 0, imgProps.width, pageCanvas.height
        );
  
        const pageData = pageCanvas.toDataURL('image/png');
  
        if (position > 0) pdf.addPage();
        pdf.addImage(pageData, 'PNG', margin, margin, pageWidth - 2 * margin, pageHeight - 2 * margin);
  
        position += pageHeight - 2 * margin;
        heightLeft -= pageHeight - 2 * margin;
      }
  
      pdf.save(`${className}-Team-Overview.pdf`);
  
      if (subtabs) subtabs.style.display = 'flex';
      /*buttons.forEach(btn => btn.style.display = '');*/
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const LoadingOverlay = ({ text = "Loading…" }) => {
    return (
      <div className="loading-overlay">
        <div className="spinner" />
        <p className="loading-text">{text}</p>
      </div>
    );
  };
  
  return (
    <>
    {exportDialogOpen && (
      <div className="dialog-overlay">
        <div className="dialog-box">
          <h3>Select sections to include</h3>
          {Object.keys(includedSections).map((key) => (
            <label key={key} style={{ display: 'block', margin: '8px 0' }}>
              <input
                type="checkbox"
                checked={includedSections[key]}
                onChange={() => handleToggleSection(key)}
              />
              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
            </label>
          ))}

          <div style={{ marginTop: '15px' }}>
            <button
              className="submit-button"
              onClick={() => {
                setExportDialogOpen(false);
                downloadPDF();  // Trigger download only after selection
              }}
            >
              Generate PDF
            </button>
            <button
              className="close-button"
              onClick={() => setExportDialogOpen(false)}
              style={{ marginLeft: '10px' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    {isGeneratingPDF && <LoadingOverlay text="Generating PDF…" />}
    <div className="reports-container">
    {!hideControls && (
      
      <div className="reports-subtabs">
        {['all', 'Daily Progress', 'Team Confirmation', 'Team Composition', 'Unassigned Students', 'Questionnaire Responses', 'Questionnaire Feedback', 'Team Allocation Feedback'].map((tab) => (
          <button
            key={tab}
            className={`filter-button ${activeSubTab === tab ? 'active' : ''}`}
            onClick={() => setActiveSubTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
        <button 
          className="pdf-button"
          onClick={() => setExportDialogOpen(true)}
        >
          Export as PDF
        </button>
        {isGeneratingPDF && (
          <div style={{ marginTop: '10px', fontSize: '14px', color: '#1976d2' }}>
            Generating PDF, please wait...
          </div>
        )}
      </div>
      )}
      {isArchived && (
        <div className="report-header">
          <h2>{className} – Overview Report</h2>
          <p><strong>Date Archived:</strong> {new Date().toLocaleString()}</p>
          <p><strong>Total Students:</strong> {students.length}</p>
          <p><strong>Total Teams:</strong> {teams.length}</p>
          {questionnaireId && <p><strong>Questionnaire ID:</strong> {questionnaireId}</p>}
        </div>
      )}

      <div className="reports-content" ref={contentRef}>
      {(activeSubTab === 'Daily Progress' || (activeSubTab === 'all' && includedSections.dailyProgress)) && (
        <div className="chart-container">
          <h3 className="section-heading">Daily Progress</h3>
            <div className="chart-controls">
                <Button 
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentDayIndex(prev => Math.max(prev - 1, 0))} 
                  disabled={currentDayIndex === 0 || dailyProgress.length === 0}
                >
                  ← Previous
                </Button>

                <span className="chart-date-label">
                  {dailyProgress.length > 0 
                    ? `Progress on ${dailyProgress[currentDayIndex]?.date}` 
                    : 'Loading Daily Progress...'}
                </span>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentDayIndex(prev => Math.min(prev + 1, dailyProgress.length - 1))} 
                  disabled={currentDayIndex === dailyProgress.length - 1 || dailyProgress.length === 0}
                >
                  Next →
                </Button>
            </div>
              
            <div className="chart-wrapper">
              {dailyChartData ? (
                <Bar data={dailyChartData} options={dailyChartOptions} />
              ) : (
                <div>
                  {dailyProgress.length === 0 ? 'No data available' : 'Loading chart...'}
                </div>
              )}
            </div>
          </div>
      )}
        
        {(activeSubTab === 'Team Confirmation' || (activeSubTab === 'all' && includedSections.teamConfirmation)) && (
          <div className="chart-container">
            <h3 className="section-heading"> Team Confirmation Status </h3>
            <div className="chart-wrapper">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        )}

        {(activeSubTab === 'Team Composition' || (activeSubTab === 'all' && includedSections.teamComposition)) && (
          <div className='team-overview'>
            <h3 className="section-heading">Team Composition</h3>
            <div className="teams-grid">
              {teams.map((team) => (
                <div key={team.teamNumber} className="team-card">
                    <h4>Team {team.teamNumber}</h4>
                    <ul>
                        {team.students.map((student) => {
                            // Find the full student data from the students prop
                            const studentData = students.find(s => s.uid === student.uid) || {};
                            return (
                                <li key={student.uid}>
                                    {student.fullName}
                                    <span className={`status-badge ${
                                        studentData.hasConfirmedGroup ? 'confirmed' : 'pending'
                                    }`}>
                                        {studentData.hasConfirmedGroup ? ' Confirmed' : ' Pending'}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
              ))}
            </div>
          </div>
        )}   

        {(activeSubTab === 'Unassigned Students' || (activeSubTab === 'all' && includedSections.unassignedStudents)) && (
          <div className="unassigned-students">
            <h3 className="section-heading">Unassigned Students ({unassignedStudents.length})</h3>
            {unassignedStudents.length > 0 ? (
              <table className="student-table">
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>Student ID</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {unassignedStudents.map((student, index) => (
                    <tr key={index}>
                      <td>{student.name}</td>
                      <td>{student.id}</td>
                      <td className="not-started">Not in Team</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-students">All students have been assigned to teams.</p>
            )}
          </div>
        )}

        {(activeSubTab === 'Questionnaire Responses' || (activeSubTab === 'all' && includedSections.questionnaireResponses)) && (
            <div className="questionnaire-responses">
              <h3 className="section-heading">Questionnaire Responses</h3>
              {/* Implement questionnaire responses here */}
              {/* This is a placeholder. You can replace it with actual content or components. */}
              implement questionnaire responses here
            </div>
        )}
        
        {(activeSubTab === 'Questionnaire Feedback' || (activeSubTab === 'all' && includedSections.questionnaireFeedback)) && (
        <div className="questionnaire-feedback">
          <h3 className="section-heading">Questionnaire Feedback</h3>

          {students.length > 0 && feedbackList.length > 0 ? (
            <div className="feedback-list">
              {feedbackList
                .filter(fb => fb.text && fb.text.trim() !== "")  // Only show non-empty feedback
                .sort((a, b) => {
                  const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
                  const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
                  return dateB - dateA; // Newest first
                })
                .map((item, index) => {
                  const student = students.find(s => s.uid === item.uid);
                  const studentName = student ? student.name || student.fullName || "Anonymous" : "Anonymous";


                  const dateObj = item.timestamp?.toDate
                    ? item.timestamp.toDate()
                    : new Date(item.timestamp);

                  const now = new Date();
                  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const yesterday = new Date(today);
                  yesterday.setDate(today.getDate() - 1);

                  let displayDate = dateObj.toLocaleDateString('en-GB');
                  if (dateObj >= today) {
                    displayDate = "Today";
                  } else if (dateObj >= yesterday) {
                    displayDate = "Yesterday";
                  }

                  return (
                    <div key={index} className="feedback-item">
                      <div className="feedback-header">
                        <div className="feedback-user">
                          <strong>{studentName}</strong>
                        </div>
                        <div className="feedback-date">{displayDate} - {dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                      <div className="feedback-text">
                        {item.text}
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p>No feedback available yet.</p>
          )}
        </div>
      )}

        {(activeSubTab === 'Team Allocation Feedback' || (activeSubTab === 'all' && includedSections.teamAllocationFeedback)) && (
          <div className='team-allocation-feedback'>
            <h3 className="section-heading">Team Allocation Feedback</h3>
            {/* Implement team allocation feedback here */}
            {/* This is a placeholder. You can replace it with actual content or components. */}
            implement team allocation feedback here
          </div>
        )}
      </div>

    </div>
    </>
  );
};

export default Reports;